import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "../../intl";
import {
  VENDOR_WORKSPACE_NAV_STORAGE_KEY,
  buildVendorDirectoryBreadcrumbs,
} from "../../constants/vendorDirectoryNavConfig";
import PartnerVendorDirectorySubnav from "./PartnerVendorDirectorySubnav";

function readNavExpandedPreference() {
  try {
    const stored = localStorage.getItem(VENDOR_WORKSPACE_NAV_STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
  } catch {
    // ignore storage errors
  }
  return false;
}

function VendorWorkspaceBreadcrumbs({ crumbs }) {
  if (!crumbs.length) {
    return null;
  }

  return (
    <nav className="partner-vendor-workspace__breadcrumbs" aria-label="Breadcrumb">
      <ol className="partner-vendor-workspace__breadcrumb-list">
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1 || crumb.current;
          return (
            <li key={`${crumb.label}-${index}`} className="partner-vendor-workspace__breadcrumb-item">
              {isLast || !crumb.to ? (
                <span
                  className="partner-vendor-workspace__breadcrumb-current"
                  aria-current={isLast ? "page" : undefined}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link className="partner-vendor-workspace__breadcrumb-link" to={crumb.to}>
                  {crumb.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function PartnerVendorWorkspaceChrome({
  clientViewId,
  relativePath = "",
  hubPathBuilder,
  dashboardPath,
  dashboardLabel,
  exitPath,
  exitLabel,
  subnavVariant = "compact",
  admin = false,
}) {
  const { t } = useLocale();
  const [navExpanded, setNavExpanded] = useState(readNavExpandedPreference);

  useEffect(() => {
    try {
      localStorage.setItem(VENDOR_WORKSPACE_NAV_STORAGE_KEY, String(navExpanded));
    } catch {
      // ignore storage errors
    }
  }, [navExpanded]);

  const crumbs = useMemo(
    () =>
      buildVendorDirectoryBreadcrumbs({
        relativePath,
        t,
        hubPathBuilder,
        clientViewId,
        dashboardPath,
        dashboardLabel,
      }),
    [relativePath, t, hubPathBuilder, clientViewId, dashboardPath, dashboardLabel]
  );

  const currentTitle = crumbs.length ? crumbs[crumbs.length - 1].label : dashboardLabel;

  return (
    <>
      <header
        className={`partner-vendor-workspace__chrome${admin ? " partner-vendor-workspace__chrome--admin" : ""}`}
      >
        <div className="partner-vendor-workspace__chrome-main">
          <VendorWorkspaceBreadcrumbs crumbs={crumbs} />
          <p className="partner-vendor-workspace__chrome-title">{currentTitle}</p>
        </div>
        <div className="partner-vendor-workspace__chrome-actions">
          <button
            type="button"
            className="partner-vendor-workspace__nav-toggle"
            aria-expanded={navExpanded}
            aria-controls="vendor-workspace-section-nav"
            onClick={() => setNavExpanded((current) => !current)}
          >
            {navExpanded
              ? t("realEstate.casaMapa.admin.vendorDirectoryWorkspace.hideSections")
              : t("realEstate.casaMapa.admin.vendorDirectoryWorkspace.showSections")}
          </button>
          <Link className="partner-vendor-workspace__exit" to={exitPath}>
            {exitLabel}
          </Link>
        </div>
      </header>

      {navExpanded ? (
        <div id="vendor-workspace-section-nav">
          <PartnerVendorDirectorySubnav
            clientViewId={clientViewId}
            hubPathBuilder={hubPathBuilder}
            variant={subnavVariant}
            activeSegment={relativePath}
          />
        </div>
      ) : null}
    </>
  );
}
